/**
 * Initialize video carousel with Splide
 * @param {string} sectionId - The section ID to target
 * @param {Object} options - Configuration options
 * @param {boolean} options.autoplay - Whether carousel should autoplay
 * @param {number} options.autoplaySpeed - Autoplay interval in milliseconds
 * @param {string} options.blockType - Block type ('video' or 'testimonial')
 * @param {number} options.gap - Gap between tiles in px
 * @param {number} options.tileWidth - Fixed tile width in px, desktop only
 */
function initVideoCarousel(sectionId, options = {}) {
  const splideElement = document.querySelector(`.video-carousel--${sectionId} .splide`);
  if (!splideElement) return;

  const isTestimonial = options.blockType === 'testimonial';

  const splideConfig = {
    type: 'loop',
    perPage: isTestimonial ? 3 : 5,
    // A fixed width keeps the centre tile and the card under it the same size at
    // every desktop width; the tiles either side simply run off the edges.
    fixedWidth: isTestimonial ? undefined : `${options.tileWidth ?? 300}px`,
    focus: 'center',
    gap: isTestimonial ? '20px' : `${options.gap ?? 15}px`,
    padding: isTestimonial ? { left: '5%', right: '5%' } : '0',
    pagination: false,
    arrows: true,
    drag: true,
    wheel: false,
    trimSpace: false,
    updateOnMove: true,
    breakpoints: {
      749: {
        fixedWidth: undefined,
        perPage: isTestimonial ? 2 : 1,
        gap: isTestimonial ? '15px' : `${Math.min(options.gap ?? 15, 10)}px`,
        focus: 'center',
        padding: isTestimonial ? { left: '3%', right: '3%' } : '10%',
        drag: true,
        wheel: false,
        trimSpace: false,
        updateOnMove: true,
      },
    },
  };

  if (options.autoplay) {
    splideConfig.autoplay = true;
    const autoplaySpeed = options.autoplaySpeed || 3000;
    splideConfig.interval = Math.max(1000, autoplaySpeed);
    splideConfig.pauseOnHover = true;
  }

  const splide = new Splide(splideElement, splideConfig);

  function pauseAllVideos() {
    const allSlides = splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    allSlides.forEach((slide) => {
      if (!slide.classList.contains('is-centre')) {
        const videoElement = slide.querySelector('deferred-media');
        if (videoElement) {
          if (videoElement.pauseMedia) {
            videoElement.pauseMedia();
          }
          const video = videoElement.querySelector('video');
          if (video) {
            video.pause();
            video.currentTime = 0;
          }
        }
      }
    });

    const clonedSlides = splideElement.querySelectorAll('.splide__slide--clone');
    clonedSlides.forEach((slide) => {
      const videoElement = slide.querySelector('deferred-media');
      if (videoElement) {
        if (videoElement.pauseMedia) {
          videoElement.pauseMedia();
        }
        const video = videoElement.querySelector('video');
        if (video) {
          video.pause();
          video.currentTime = 0;
        }
      }
    });
  }

  function getRealSlide(index) {
    const allSlides = splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    if (allSlides[index]) {
      return allSlides[index];
    }
    return null;
  }

  const isMobile =
    window.innerWidth <= 749 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

  function preloadVideo(slide, shouldPlay = false) {
    if (!slide || slide.classList.contains('splide__slide--clone')) return;
    if (!shouldPlay && !slide.classList.contains('is-centre')) return;
    const videoElement = slide.querySelector('deferred-media');
    if (!videoElement) return;

    if (!videoElement.getAttribute('data-media-loaded')) {
      if (videoElement.loadContent) {
        videoElement.loadContent(false);
      } else if (videoElement.showDeferredMedia && shouldPlay) {
        videoElement.showDeferredMedia();
      }

      requestAnimationFrame(() => {
        const video = videoElement.querySelector('video');
        if (video) {
          if (!video.hasAttribute('preload')) {
            video.setAttribute('preload', isMobile ? 'metadata' : 'auto');
          }
          if (!shouldPlay && !slide.classList.contains('is-centre')) {
            video.pause();
            video.removeAttribute('autoplay');
          }
        }
      });
    } else if (!shouldPlay && !slide.classList.contains('is-centre')) {
      const video = videoElement.querySelector('video');
      if (video) {
        video.pause();
        video.removeAttribute('autoplay');
      }
      if (videoElement.pauseMedia) {
        videoElement.pauseMedia();
      }
    }
  }

  function playActiveVideo(slideElement = null) {
    let activeSlide = null;

    if (slideElement && !slideElement.classList.contains('splide__slide--clone')) {
      activeSlide = slideElement;
    } else {
      const activeSlides = splideElement.querySelectorAll('.splide__slide.is-centre:not(.splide__slide--clone)');
      if (activeSlides.length > 0) {
        activeSlide = activeSlides[0];
      } else {
        const currentIndex = splide.index;
        activeSlide = getRealSlide(currentIndex);
      }
    }

    if (!activeSlide || activeSlide.classList.contains('splide__slide--clone')) return;

    const videoElement = activeSlide.querySelector('deferred-media');
    if (!videoElement) return;

    const currentIndex = splide.index;
    const totalSlides = splide.length;
    const nextIndex = (currentIndex + 1) % totalSlides;
    const prevIndex = (currentIndex - 1 + totalSlides) % totalSlides;

    const nextSlide = getRealSlide(nextIndex);
    if (nextSlide && !nextSlide.classList.contains('is-centre')) {
      requestAnimationFrame(() => preloadVideo(nextSlide, false));
    }

    const prevSlide = getRealSlide(prevIndex);
    if (prevSlide && !prevSlide.classList.contains('is-centre')) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => preloadVideo(prevSlide, false));
      });
    }

    if (!videoElement.getAttribute('data-media-loaded')) {
      if (videoElement.showDeferredMedia) {
        videoElement.showDeferredMedia();
      }

      let attempts = 0;
      const maxAttempts = 50;
      const playVideo = () => {
        attempts++;
        if (videoElement.getAttribute('data-media-loaded')) {
          requestAnimationFrame(() => {
            if (videoElement.playMedia) {
              videoElement.playMedia();
            }
          });
        } else if (attempts < maxAttempts) {
          requestAnimationFrame(playVideo);
        } else {
          if (videoElement.loadContent) {
            videoElement.loadContent(false);
          }
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (videoElement.playMedia) {
                videoElement.playMedia();
              }
            });
          });
        }
      };
      requestAnimationFrame(playVideo);
    } else {
      requestAnimationFrame(() => {
        if (videoElement.playMedia) {
          videoElement.playMedia();
        }
      });
    }
  }

  splide.on('moved', () => {
    pauseAllVideos();
    playActiveVideo();
  });

  // Splide passes its Slide component here, not the element, so read .slide
  splide.on('active', (activeSlide) => {
    const slideElement = activeSlide && activeSlide.slide ? activeSlide.slide : activeSlide;
    if (slideElement && slideElement.classList && slideElement.classList.contains('splide__slide--clone')) {
      const videoElement = slideElement.querySelector('deferred-media');
      if (videoElement && videoElement.pauseMedia) {
        videoElement.pauseMedia();
      }
      return;
    }
    pauseAllVideos();
    playActiveVideo(slideElement);
  });

  splide.on('mounted', () => {
    const playFirstVideo = () => {
      const firstSlide = getRealSlide(0);
      if (!firstSlide) {
        requestAnimationFrame(playFirstVideo);
        return;
      }

      // An image slide has nothing to play
      const videoElement = firstSlide.querySelector('deferred-media');
      if (!videoElement) return;

      if (!videoElement.getAttribute('data-media-loaded')) {
        if (videoElement.loadContent) {
          videoElement.loadContent(false);
        } else if (videoElement.showDeferredMedia) {
          videoElement.showDeferredMedia();
        }

        let attempts = 0;
        const maxAttempts = 100;
        const tryPlay = () => {
          attempts++;
          if (videoElement.getAttribute('data-media-loaded')) {
            requestAnimationFrame(() => {
              if (videoElement.playMedia) {
                videoElement.playMedia();
              }
            });
          } else if (attempts < maxAttempts) {
            requestAnimationFrame(tryPlay);
          } else {
            if (videoElement.loadContent) {
              videoElement.loadContent(false);
            }
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                if (videoElement.playMedia) {
                  videoElement.playMedia();
                }
              });
            });
          }
        };
        requestAnimationFrame(tryPlay);
      } else {
        requestAnimationFrame(() => {
          if (videoElement.playMedia) {
            videoElement.playMedia();
          }
        });
      }
    };

    // Load and play the first video only as the carousel nears the screen,
    // rather than on page load when it may be far down the page.
    whenNearScreen(() => {
      requestAnimationFrame(() => {
        requestAnimationFrame(playFirstVideo);
      });
    });
  });

  function whenNearScreen(callback) {
    if (!('IntersectionObserver' in window)) {
      callback();
      return;
    }
    const nearObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          nearObserver.disconnect();
          callback();
        }
      },
      { rootMargin: '400px 0px' }
    );
    nearObserver.observe(splideElement);
  }

  splide.mount();

  /*
    Clicking a video that is not centred brings it to the centre and plays it.
    Clones carry the index of the slide they copy, so a click on either resolves
    to the same real slide. The product card sits outside the video wrapper, so
    its link is untouched by this.
  */
  /*
    Splide marks every slide on the current page `is-active`, which with five
    per page is five slides at once. The centre treatment needs the one focused
    slide, so mark that ourselves and let the CSS key off `is-centre`.
  */
  const markCentreSlide = () => {
    const slides = splide.Components.Slides.get(true);
    slides.forEach(({ slide, index }) => {
      slide.classList.toggle('is-centre', index === splide.index);
    });
  };

  splide.on('mounted moved active refresh', markCentreSlide);
  markCentreSlide();

  const focusSlideOnClick = () => {
    const slides = splide.Components.Slides.get(true);
    slides.forEach(({ slide, index }) => {
      const wrapper = slide.querySelector('.video-carousel__video-wrapper');
      if (!wrapper || wrapper.dataset.focusBound === 'true') return;
      wrapper.dataset.focusBound = 'true';

      wrapper.addEventListener(
        'click',
        (event) => {
          if (slide.classList.contains('is-centre')) return;
          event.preventDefault();
          event.stopPropagation();
          splide.go(index);
        },
        true
      );
    });
  };

  focusSlideOnClick();
  splide.on('refresh', focusSlideOnClick);

  const setupIntersectionObserver = () => {
    const observerOptions = {
      root: splideElement,
      rootMargin: isMobile ? '100%' : '50%',
      threshold: 0.1,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const slide = entry.target;
          if (slide.classList.contains('splide__slide--clone')) return;
          if (!slide.classList.contains('is-centre')) return;
          const videoElement = slide.querySelector('deferred-media');
          if (videoElement && !videoElement.getAttribute('data-media-loaded')) {
            preloadVideo(slide);
          }
        }
      });
    }, observerOptions);

    const allSlides = splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    allSlides.forEach((slide) => {
      observer.observe(slide);
    });
  };

  whenNearScreen(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(setupIntersectionObserver);
    });
  });

  const pauseNonActiveVideos = () => {
    const allSlides = splideElement.querySelectorAll('.splide__slide:not(.splide__slide--clone)');
    allSlides.forEach((slide) => {
      if (!slide.classList.contains('is-centre')) {
        const videoElement = slide.querySelector('deferred-media');
        if (videoElement) {
          if (videoElement.pauseMedia) {
            videoElement.pauseMedia();
          }
          const video = videoElement.querySelector('video');
          if (video) {
            if (!video.paused) video.pause();
            if (video.currentTime) video.currentTime = 0;
            video.removeAttribute('autoplay');
          }
        }
      }
    });

    const clonedSlides = splideElement.querySelectorAll('.splide__slide--clone');
    clonedSlides.forEach((slide) => {
      const videoElement = slide.querySelector('deferred-media');
      if (videoElement) {
        if (videoElement.pauseMedia) {
          videoElement.pauseMedia();
        }
        const video = videoElement.querySelector('video');
        if (video) {
          video.pause();
          video.currentTime = 0;
          video.removeAttribute('autoplay');
        }
      }
    });
  };

  splide.on('moved', pauseNonActiveVideos);
  splide.on('active', pauseNonActiveVideos);

  // A video that starts outside the centre tile (clones copy the autoplay
  // attribute) is stopped as it starts. This replaces a check that ran on every
  // animation frame and kept the main thread busy on phones.
  splideElement.addEventListener(
    'play',
    (event) => {
      const video = event.target;
      if (!(video instanceof HTMLVideoElement)) return;
      const slide = video.closest('.splide__slide');
      if (slide && (slide.classList.contains('splide__slide--clone') || !slide.classList.contains('is-centre'))) {
        video.pause();
      }
    },
    true
  );

  const updateLinePagination = () => {
    const linePagination = document.querySelector(`.video-carousel--${sectionId} .video-carousel__line-pagination`);
    if (!linePagination) return;

    const lineFill = linePagination.querySelector('.video-carousel__line-fill');
    if (!lineFill) return;

    const totalSlides = splide.length;
    const currentIndex = splide.index;

    let position;
    if (totalSlides === 1) {
      position = 50;
    } else {
      position = (currentIndex / (totalSlides - 1)) * 100;
    }

    const segmentWidth = 33.33;
    const segmentLeft = Math.max(0, Math.min(100 - segmentWidth, position - segmentWidth / 2));

    if (currentIndex === 0) {
      lineFill.style.left = '0%';
    } else if (currentIndex === totalSlides - 1) {
      lineFill.style.left = `${100 - segmentWidth}%`;
    } else {
      lineFill.style.left = `${segmentLeft}%`;
    }

    lineFill.style.width = `${segmentWidth}%`;
  };

  splide.on('moved', () => {
    updateLinePagination();
  });

  splide.on('active', () => {
    updateLinePagination();
  });

  updateLinePagination();
}
